import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Mail, Pin, PinOff, Trash2, Check, RotateCcw, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminComms() {
  const [announcements, setAnnouncements] = useState(null);
  const [messages, setMessages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/announcements").then((r) => setAnnouncements(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("body", form.body);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post("/admin/announcements", fd);
      toast.success("Annonce publiée : elle s'affiche maintenant sur le site.");
      setForm({ title: "", body: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/admin/announcements/${id}`, body);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeAnnouncement = async (a) => {
    if (!window.confirm(`Supprimer l'annonce « ${a.title} » ?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Annonce supprimée.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleMessage = async (m) => {
    try {
      await api.patch(`/admin/messages/${m.id}?is_handled=${!m.is_handled}`, {});
      toast.success(m.is_handled ? "Message rouvert." : "Message marqué comme traité.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unhandled = (messages || []).filter((m) => !m.is_handled).length;

  return (
    <div className="space-y-9" data-testid="admin-comms-view">
      <section data-testid="announcements-admin-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-korban-navy">
          <Megaphone size={19} className="text-korban-blue" /> Annonces du site
        </h2>
        <form onSubmit={publish} data-testid="announcement-form" className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div>
            <label htmlFor="a-title" className="mb-1 block text-xs font-bold text-slate-600">Titre de l'annonce</label>
            <input id="a-title" required minLength={3} value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex. Ouverture d'un nouveau poste de gestionnaire de paie"
              data-testid="announcement-input-title" className="field field-light" />
          </div>
          <div className="mt-4">
            <label htmlFor="a-body" className="mb-1 block text-xs font-bold text-slate-600">Message</label>
            <textarea id="a-body" required minLength={3} rows={4} value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Texte du communiqué affiché sur le site…"
              data-testid="announcement-input-body" className="field field-light resize-none" />
          </div>
          <div className="mt-4">
            <label htmlFor="a-img" className="mb-1 block text-xs font-bold text-slate-600">Image de couverture (facultatif)</label>
            <input id="a-img" type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.webp,.gif"
              data-testid="announcement-input-image"
              className="block w-full text-xs text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-korban-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-korban-gold" />
          </div>
          <button type="submit" disabled={busy} data-testid="announcement-submit-button"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-6 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Megaphone size={16} />}
            Publier sur le site
          </button>
        </form>

        {!announcements ? (
          <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : announcements.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="announcements-admin-empty">
            Aucune annonce publiée pour l'instant.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`announcement-row-${a.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-korban-navy">{a.title}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-500">{a.body}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1.5">
                    <span className={`rounded-full px-2.5 py-1 text-[0.65rem] font-bold ${a.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`announcement-state-${a.id}`}>
                      {a.published ? "Publiée" : "Masquée"}
                    </span>
                    {a.pinned && <span className="rounded-full bg-korban-gold/20 px-2.5 py-1 text-[0.65rem] font-bold text-korban-golddark">À la une</span>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => patch(a.id, { published: !a.published }, a.published ? "Annonce masquée." : "Annonce publiée.")}
                    data-testid={`announcement-toggle-${a.id}`}
                    className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 hover:bg-slate-50">
                    {a.published ? "Masquer" : "Publier"}
                  </button>
                  <button onClick={() => patch(a.id, { pinned: !a.pinned }, a.pinned ? "Retirée de la une." : "Épinglée en tête des annonces.")}
                    data-testid={`announcement-pin-${a.id}`}
                    className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 hover:bg-slate-50">
                    {a.pinned ? <PinOff size={13} className="inline" /> : <Pin size={13} className="inline" />} {a.pinned ? "Désépingler" : "Épingler"}
                  </button>
                  <button onClick={async () => {
                    try {
                      const r = await api.post(`/admin/announcements/${a.id}/send`);
                      toast.success(`${r.data.sent} email(s) envoyé(s) aux partenaires${r.data.failed ? ` — ${r.data.failed} échec(s)` : ""}.`);
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail));
                    }
                  }} data-testid={`announcement-send-${a.id}`}
                    className="rounded-lg bg-korban-blue px-3 py-2 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:brightness-110">
                    <Send size={13} className="inline" /> Envoyer aux partenaires
                  </button>
                  <button onClick={() => removeAnnouncement(a)} data-testid={`announcement-delete-${a.id}`}
                    className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 hover:bg-red-50">
                    <Trash2 size={13} className="inline" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="messages-admin-section">
        <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-bold text-korban-navy">
          <Mail size={19} className="text-korban-blue" /> Messages reçus (contact)
          {unhandled > 0 && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700" data-testid="unhandled-badge">
              {unhandled} nouveau{unhandled > 1 ? "x" : ""}
            </span>
          )}
        </h2>
        {!messages ? (
          <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : messages.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="messages-empty">
            Aucun message reçu via le formulaire de contact.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`rounded-xl border p-4 shadow-sm ${m.is_handled ? "border-slate-200 bg-white" : "border-korban-gold/50 bg-[#fffaf0]"}`} data-testid={`message-row-${m.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-korban-navy">{m.name}</p>
                    <p className="truncate text-xs text-slate-500">
                      <a href={`mailto:${m.email}`} className="text-korban-blue hover:underline">{m.email}</a>
                      {m.phone ? ` · ${m.phone}` : ""}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[0.65rem] font-bold ${m.is_handled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`message-state-${m.id}`}>
                    {m.is_handled ? "Traité" : "Nouveau"}
                  </span>
                </div>
                <p className="mt-2.5 whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">{m.message}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-400">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <button onClick={() => toggleMessage(m)} data-testid={`message-toggle-${m.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 hover:bg-slate-50">
                    {m.is_handled ? <><RotateCcw size={12} /> Rouvrir</> : <><Check size={12} /> Marquer traité</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
